<script setup lang="ts">
import {computed, PropType, ref} from "vue";
import {ICellRendererParams} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import AttachmentLinkUtil from "@/utils/UIConstants/AttachmentLinkUtil.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import ImagePreviewer from "@/components/dialogs/ImagePreviewer.vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {Dialog} from "quasar";
import ReferenceDialog from "@/components/dialogs/ReferenceDialog.vue";
import ReferenceGridFieldWidgetUtil from "@/utils/editView/widgets/ReferenceGridFieldWidgetUtil.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import EntityReferenceData = namaTypes.EntityReferenceData;
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import FieldMetaData = namaTypes.FieldMetaData;

const props = defineProps({
  params: {
    type: Object as PropType<ICellRendererParams>,
    required: true
  }
});
const record: BaseEntityDTO | undefined = props.params['record'];
const entityTypeColId: string | undefined = props.params['entityTypeColId'];
let referencedEntityType = props.params['referencedEntityType'];
if (!referencedEntityType && record && entityTypeColId)
  referencedEntityType = NamaCommon.getFieldValue(record, entityTypeColId, props.params.node.rowIndex!);
const isShowCode = props.params['isShowCode'];
let mouseIn = false;
const imageVisible = ref(false)
/* TODO:: NaMaReferenceCell.renderNormalMode => canEdit */
let formattedValue: any = "";
let fieldValue = props.params?.value as EntityReferenceData | null | undefined;
if (fieldValue && isShowCode)
  formattedValue = Translator.getCode(fieldValue);
else if (fieldValue)
  formattedValue = Translator.getNameForFields(fieldValue);
const highlightedHtml = AGGridUtils.getFindHighlightedHtml(props.params.api, props.params.node, props.params.column, formattedValue ?? '');
let handler: EditScreenGridHandler = props.params['handler']();
const columnMetadata: FieldMetaData = props.params['columnMetadata'];
const colId = props.params["columnId"];

const fieldUtil = new ReferenceGridFieldWidgetUtil(handler, columnMetadata, ref(fieldValue), props.params!.node.rowIndex!);
fieldUtil.params = props.params;
const errorsStore = useErrorsStore();
const isColumnDisabled = computed(()=>handler.presenter.isColumnDisabled(props.params.data.id, handler.gridId, colId))
function mouseEnter() {
  if (!ReferenceFieldWidgetUtil.shouldShowImgInGrid(referencedEntityType))
    return;
  mouseIn = true;
  if (imageVisible.value)
    return;
  setTimeout(() => {
    if (!mouseIn)
      return;
    imageVisible.value = true;
    return;
  }, 1000);
}

function mouseLeave() {
  if (!ReferenceFieldWidgetUtil.shouldShowImgInGrid(referencedEntityType))
    return;
  mouseIn = false;
}

function hasColorOrImg(ref: EntityReferenceData) {
  return ReferenceFieldWidgetUtil.shouldShowImgInGrid(referencedEntityType) || (ref && ref.colorCode);
}

function onBrowseClicked(e: MouseEvent | KeyboardEvent, isInNewMode?: boolean): void {
  if (!referencedEntityType) {
    errorsStore.pushErrorStr("You must select Entity Type");
    e.preventDefault();
    return;
  }
  if (e.ctrlKey || (e as KeyboardEvent).key === "F6") {
    const linkToObject = PlaceUtils.linkToObject(fieldValue ?? undefined);
    let url = linkToObject;
    if (ObjectChecker.isEmptyOrNull(linkToObject))
      url = PlaceUtils.linkToPlace(PlaceUtils.linkToNew(fieldUtil.referencedEntityType.value, fieldUtil.presenter.entityType));
    window.open(url, "_blank");
    return;
  }
  Dialog.create({
    component: ReferenceDialog,
    componentProps: {
      fieldUtil: fieldUtil,
      event: e,
      isInNewMode,
      field: (handler.listVisibleColumns()).find(l => l.id === columnMetadata.id),
      isFieldEnabled: !isColumnDisabled.value
    }
  });
}

defineExpose({onBrowseClicked});
</script>

<template>
  <div class="tw-flex tw-w-full tw-items-center tw-h-full" :class="{'tw-justify-between tw-gap-1':fieldValue,'tw-justify-end':!fieldValue}">
    <template v-if="fieldValue">
      <div class="tw-flex tw-items-center tw-gap-1 tw-overflow-hidden">
        <q-avatar round class="tw-size-7 compact:tw-size-5 ultra:tw-size-4 tw-ring-1 tw-ring-gray-400 dark:tw-ring-0" v-if="hasColorOrImg(fieldValue)"
                  @mouseenter="mouseEnter" @mouseleave="mouseLeave" :style="fieldValue.colorCode?'backgroundColor:'+fieldValue.colorCode:''">
          <img class="tw-w-full tw-h-full" v-if="ReferenceFieldWidgetUtil.shouldShowImgInGrid(referencedEntityType)"
               :src="AttachmentLinkUtil.downloadURLByRecordId(fieldValue, DownloadPurpose.THUMBNAIL, false, props.params?.data?.item?.itemCode)"/>
        </q-avatar>
        <span v-if="highlightedHtml" v-html="highlightedHtml"/>
        <span v-else v-text="formattedValue"/>
      </div>
    </template>
    <div class="tw-flex tw-items-center tw-gap-1">
      <q-avatar
        square v-if="!fieldUtil.formattedValue().value"
        @click.stop="(event)=>onBrowseClicked(event,true)"
        class="nama-avatar-hover tw-size-7 tw-cursor-pointer">
        <NamaIcon default-icon="nm-reply" class="tw-size-4 active-primary"/>
      </q-avatar>
      <q-avatar square class="nama-avatar-hover tw-size-7 tw-cursor-pointer"
                :title="`Open in new tab [F6] / [Ctrl+Click]\nEdit in Popup [F7] / [Alt+Click]\nNew [F8]`"
                @click.stop="onBrowseClicked">
        <NamaIcon default-icon="nm-advanced-search" class="tw-size-4 active-primary"/>
    </q-avatar>
    </div>
    <ImagePreviewer v-if="fieldValue" v-model="imageVisible"
                    :src="AttachmentLinkUtil.downloadURLByRecordId(fieldValue, DownloadPurpose.THUMBNAIL, false, props.params?.data?.item?.itemCode)"/>
  </div>
</template>

<style scoped>

</style>
